import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { getSignUpRequestDetail } from '../../../../services/signUpRequestDetail/signUpRequestDetail';
import { ExplorerSignupRequestSerializer } from '@bcba/serializers';
import { useToastsHook, useTranslation } from '@bcba/react-hooks';
import {
  FieldArrayWithId,
  UseFormHandleSubmit,
  UseFormReturn,
  useDisclosure,
  useFieldArray,
  useForm,
} from '@agrotoken-tech/ui';
import { ExplorerUpdateSignUpDto } from '../../../../services/dtos/getExplorerUpdateSignupDto';
import { updateSignUpRequest } from '../../../../services/signUpRequestDetail/updateSignUpRequest';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';

export interface SignUpRequestDetailHook {
  bdData: ExplorerSignupRequestSerializer | undefined;
  isLoading: boolean;
  isMutateLoading: boolean;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSubmit: (data: ExplorerSignupRequestSerializer) => void;
  handleSubmit: UseFormHandleSubmit<ExplorerSignupRequestSerializer, undefined>;
  formMethods: UseFormReturn<ExplorerSignupRequestSerializer, any, undefined>;
  fields: FieldArrayWithId<
    ExplorerSignupRequestSerializer,
    'legalRepresentatives',
    'id'
  >[];
  shouldDisableEdit: boolean;
}

const resolver = classValidatorResolver(ExplorerUpdateSignUpDto);

export const useSignUpRequestDetail = (): SignUpRequestDetailHook => {
  const { showSuccessToast } = useToastsHook();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useTranslation();

  // Fetch sign-up request details
  const {
    data: bdData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['sign-up-request-detail'],
    queryFn: getSignUpRequestDetail,
  });

  const formMethods = useForm({
    resolver,
    defaultValues: bdData || {},
  });

  const { handleSubmit, formState, watch } = formMethods;
  const { errors } = formState;

  const { fields } = useFieldArray({
    control: formMethods.control,
    name: 'legalRepresentatives',
  });

  const formHasErrors = Object.keys(errors).length > 0;

  const legalRepresentatives = watch('legalRepresentatives');
  const formHasValidationMessages = legalRepresentatives?.some(
    (rep: any) =>
      rep.idDocument?.validationMessage ||
      rep.signupPermissionDocument?.validationMessage,
  );
  // Checking name also allows us to check if the file has been uploaded.
  // By default, the Dropzone component behind the S3FileUploader sets the file as B64 first (given the field name), so if we
  // only check by this value, it will instantly marked as unsaved. Otherwise, it will wait for the upload.
  // TODO: We need to check later why the component is throwing an error when we don't parse the file as B64.
  const formHasUnsavedFiles = legalRepresentatives?.some(
    (rep: any) =>
      (rep.idDocument?.file?.s3TempKey && rep.idDocument?.file?.name) ||
      (rep.signupPermissionDocument?.file?.s3TempKey &&
        rep.signupPermissionDocument?.file?.name),
  );

  const shouldDisableEdit = Boolean(
    formHasErrors || formHasValidationMessages || !formHasUnsavedFiles,
  );

  // Update documents in sign-up request
  const { mutate: mutateSignUpRequestDetail, isPending: isMutateLoading } =
    useMutation({
      mutationFn: async (data: ExplorerUpdateSignUpDto) =>
        updateSignUpRequest(bdData?.uuid ?? '', data),
      onSuccess: () => {
        onClose();
        refetch();
        showSuccessToast({
          title: t('signUpRequest.toast.success.label'),
          text: t('signUpRequest.toast.success.text'),
        });
      },
    });

  const onSubmit = (data: any) => {
    onOpen();

    const dtoToUpdate: ExplorerUpdateSignUpDto = {
      legalRepresentatives: [],
    };

    for (const rep of data.legalRepresentatives || []) {
      const { idDocument, signupPermissionDocument } = rep;
      let representative: any;

      if (idDocument?.file?.s3TempKey) {
        const { file, uuid } = idDocument;

        representative = {
          uuid: rep.uuid,
          idDocument: { file, uuid },
        };
      }
      if (signupPermissionDocument?.file?.s3TempKey) {
        const { file, uuid } = signupPermissionDocument;
        representative = {
          ...representative,
          uuid: rep.uuid,
          signupPermissionDocument: { file, uuid },
        };
      }
      dtoToUpdate.legalRepresentatives?.push({
        ...representative,
      });
    }
    mutateSignUpRequestDetail(dtoToUpdate);
  };

  useEffect(() => {
    if (bdData) {
      formMethods.reset(
        { legalRepresentatives: bdData?.legalRepresentatives },
        { keepDirty: false },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bdData]);

  return {
    bdData,
    isLoading,
    isMutateLoading,
    isOpen,
    onOpen,
    onClose,
    onSubmit,
    handleSubmit,
    formMethods,
    fields,
    shouldDisableEdit,
  };
};

export default useSignUpRequestDetail;
